import React, { useEffect, useState } from "react";
import { prodApi, fmtKg, fmtPct, fmtEur, SELL } from "./prodApi";
import { useProd } from "./ProdApp";
import { PTitle, PCard, PSelect, PToggle, PEmpty, Spinner, PBadge } from "./ProdUi";

const GROUPS = [
  { value: "none", label: "Tutte" }, { value: "supplier", label: "Fornitore" }, { value: "origin", label: "Origine" }, { value: "operator", label: "Operatore" }, { value: "month", label: "Mese" },
];
const LAST = [{ value: "10", label: "Ultime 10" }, { value: "50", label: "Ultime 50" }, { value: "0", label: "Tutte" }];

const Delta = ({ v }) => <span className={`tabular-nums font-bold ${Math.abs(v) < 0.05 ? "text-slate-400" : v > 0 ? "text-emerald-400" : "text-red-400"}`}>{v > 0 ? "+" : ""}{v.toFixed(2)} pt</span>;

export default function ProdStats() {
  const { recipes } = useProd();
  const [recipeId, setRecipeId] = useState(recipes[0]?.id || "");
  const [groupBy, setGroupBy] = useState("none");
  const [last, setLast] = useState("10");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    if (!recipeId) return;
    setData(null);
    prodApi.get("/production/stats/yields", { params: { recipe_id: recipeId, group_by: groupBy, last } }).then((r) => setData(r.data)).catch(() => setData({ groups: [], count: 0 }));
  }, [recipeId, groupBy, last]);

  return (
    <div data-testid="prod-stats-page">
      <PTitle kicker="Database rese" title="Storico rese reali">Medie delle lavorazioni reali confrontate con la resa teorica della ricetta: per fornitore, origine, operatore o mese.</PTitle>
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-3 mb-5">
        <PSelect value={recipeId} onChange={(e) => setRecipeId(e.target.value)} data-testid="prod-stats-recipe">
          <option value="">— Ricetta —</option>
          {recipes.map((r) => <option key={r.id} value={r.id}>{r.input_node_name} → {r.name}</option>)}
        </PSelect>
        <PToggle options={GROUPS} value={groupBy} onChange={setGroupBy} testIdPrefix="prod-stats-group" />
        <PToggle options={LAST} value={last} onChange={setLast} testIdPrefix="prod-stats-last" />
      </div>
      {!recipeId && <PEmpty title="Scegli una ricetta" />}
      {recipeId && data === null && <Spinner text="Calcolo medie..." />}
      {data && data.count === 0 && <PEmpty title="Nessuna lavorazione registrata nello storico" text="Avvia una nuova 'Resa reale rapida' dal menu: ogni lavorazione chiusa alimenta queste medie." testId="prod-stats-empty" />}
      <div className="space-y-3">
        {data?.groups?.map((g) => (
          <PCard key={g.label} className="!p-0 overflow-hidden" data-testid={`prod-stats-group-${g.label}`}>
            <button className="w-full text-left p-4 flex flex-col md:flex-row md:items-center gap-3 hover:bg-slate-800/60" onClick={() => setOpen((o) => ({ ...o, [g.label]: !o[g.label] }))} data-testid={`prod-stats-toggle-${g.label}`}>
              <div className="flex-1 min-w-0">
                <p className="font-['Chivo'] font-black text-white text-lg">{g.label}</p>
                <p className="text-xs text-slate-400">{g.runs} lavorazioni · {fmtKg(g.input_kg, 1)} kg in ingresso</p>
              </div>
              <div className="grid grid-cols-3 gap-4 text-right tabular-nums">
                <div><p className="text-[10px] uppercase text-slate-500 font-bold">Resa reale</p><p className={`font-black text-lg ${g.yield_pct >= g.yield_theoretical_pct ? "text-emerald-400" : "text-amber-400"}`}>{fmtPct(g.yield_pct, 2)}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500 font-bold">Teorica</p><p className="font-black text-lg text-slate-300">{fmtPct(g.yield_theoretical_pct, 2)}</p></div>
                <div><p className="text-[10px] uppercase text-slate-500 font-bold">Margine</p><p className={`font-black text-lg ${g.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(g.margin)}</p><p className="text-[10px] text-slate-500">{fmtPct(g.margin_pct)}</p></div>
              </div>
            </button>
            {open[g.label] && (
              <div className="border-t border-slate-700 divide-y divide-slate-700/60">
                {g.rows.map((r) => (
                  <div key={r.key} className="px-4 py-2 flex items-center gap-3 text-sm" data-testid={`prod-stats-row-${r.key}`}>
                    <p className={`flex-1 ${SELL.includes(r.disposal) ? "text-white" : "text-slate-400"}`}>{r.node_name}{r.trim_level_name && <span className="text-slate-500 text-xs"> · {r.trim_level_name}</span>} {!SELL.includes(r.disposal) && <PBadge tone="amber" className="ml-1">{r.disposal === "waste" ? "scarto" : "sottoprod."}</PBadge>}</p>
                    <p className="w-24 text-right tabular-nums text-slate-300">{fmtKg(r.kg, 1)} kg</p>
                    <p className="w-24 text-right tabular-nums font-bold text-white">{fmtPct(r.pct_avg, 2)}</p>
                    <p className="w-20 text-right tabular-nums text-slate-500">{fmtPct(r.pct_theoretical, 2)}</p>
                    <p className="w-24 text-right"><Delta v={r.diff_pct_points} /></p>
                  </div>
                ))}
                <p className="px-4 py-2 text-[11px] text-slate-500">kg totali · media reale % · teorica % · scostamento</p>
              </div>
            )}
          </PCard>
        ))}
      </div>
    </div>
  );
}
